import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { TagawattDataset } from './core/_models'
import {
  filterVisibleDatasets,
  getHalfHourLabelTimestamp,
  getSeriesKey,
  isMinuteKeyIncludedForToday,
  trimHalfHourLabelsAndDataToNow,
} from './liveDataSeriesUtils'
import { useViewport, useChartTouchHandlers, ViewportInfo } from './chartViewportUtils'

type ChartPoint = { x: number; y: number }

export type LiveDataChartProps = {
  graphType: string
  resolution: '30min' | '1min'
  chartType: string
  viewDate: string
  isIntradayView: boolean
  isTodayView: boolean
  labels: string[]
  datasets: TagawattDataset[]
  seriesColors: string[]
  hiddenSeriesKeys: Set<string>
  onSeriesToggle: (seriesKey: string) => void
  isFullscreen: boolean
  onFullscreenChange: (value: boolean) => void
}

// useViewport imported from chartViewportUtils

function resolveChartHeight(
  viewport: ViewportInfo,
  isFullscreen: boolean,
  isIntradayView: boolean
): number {
  if (isFullscreen) {
    return Math.max(280, viewport.height - (viewport.isMobile ? 72 : 96))
  }
  if (viewport.isMobile) {
    if (viewport.isLandscape) {
      return Math.min(Math.max(viewport.height - 140, 260), 420)
    }
    return isIntradayView ? Math.min(Math.max(viewport.height * 0.42, 300), 380) : 320
  }
  if (viewport.isTablet) {
    return viewport.isLandscape ? 400 : 380
  }
  return 480
}

function buildIntradaySeries(
  viewDate: string,
  resolution: '30min' | '1min',
  labels: string[],
  datasets: TagawattDataset[],
  isTodayView: boolean
): { name: string; data: ChartPoint[]; seriesKey: string }[] {
  if (resolution === '1min') {
    return datasets
      .filter((ds) => ds.minute_data)
      .map((ds) => {
        let keys = Object.keys(ds.minute_data!).sort()
        if (isTodayView) {
          const nowKey = moment().format('HH:mm')
          keys = keys.filter((k) => isMinuteKeyIncludedForToday(k, nowKey))
        }
        return {
          name: ds.name,
          seriesKey: getSeriesKey(ds),
          data: keys.map((k) => ({
            x: moment(`${viewDate} ${k}`, 'YYYY-MM-DD HH:mm').valueOf(),
            y: ds.minute_data![k],
          })),
        }
      })
  }

  let effectiveLabels = labels
  if (isTodayView && effectiveLabels.length > 0 && datasets.length > 0) {
    const trimmed = trimHalfHourLabelsAndDataToNow(effectiveLabels, datasets[0].data || [])
    effectiveLabels = trimmed.labels
  }

  return datasets.map((ds) => {
    let values = ds.data || []
    if (isTodayView && effectiveLabels.length > 0) {
      values = values.slice(0, effectiveLabels.length)
    }
    return {
      name: ds.name,
      seriesKey: getSeriesKey(ds),
      data: effectiveLabels.map((label, i) => ({
        x: getHalfHourLabelTimestamp(viewDate, label, i, effectiveLabels.length),
        y: values[i] ?? 0,
      })),
    }
  })
}

const LiveDataChart: React.FC<LiveDataChartProps> = ({
  graphType,
  resolution,
  chartType,
  viewDate,
  isIntradayView,
  isTodayView,
  labels,
  datasets,
  seriesColors: seriesColorsProp,
  hiddenSeriesKeys,
  onSeriesToggle,
  isFullscreen,
  onFullscreenChange,
}) => {
  const intl = useIntl()
  const viewport = useViewport()
  const chartHeight = resolveChartHeight(viewport, isFullscreen, isIntradayView)
  const zoomStateRef = useRef<{ min: number; max: number } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const nameToKeyRef = useRef<Map<string, string>>(new Map())

  const { xMin, xMax } = useMemo(() => {
    if (isIntradayView) {
      const base = moment(viewDate, 'YYYY-MM-DD')
      const xMinVal =
        resolution === '30min'
          ? base.clone().startOf('day').add(30, 'minutes').valueOf()
          : base.clone().startOf('day').valueOf()
      const xMaxVal = isTodayView
        ? moment().valueOf()
        : resolution === '30min'
        ? base.clone().add(1, 'day').startOf('day').valueOf()
        : base.clone().endOf('day').valueOf()
      return { xMin: xMinVal, xMax: xMaxVal }
    } else {
      return { xMin: 1, xMax: labels.length }
    }
  }, [isIntradayView, resolution, viewDate, isTodayView, labels.length])

  useEffect(() => {
    zoomStateRef.current = null
  }, [viewDate, resolution, chartType, datasets.length])

  useChartTouchHandlers({
    chartId: isIntradayView ? 'live-data-intraday' : 'live-data-category',
    xMin,
    xMax,
    isMobile: viewport.isMobile,
    containerRef,
    isCategoryChart: !isIntradayView,
    onZoomed: (min, max) => {
      zoomStateRef.current = { min, max }
    },
  })

  const visibleDatasets = useMemo(
    () => filterVisibleDatasets(datasets, hiddenSeriesKeys),
    [datasets, hiddenSeriesKeys]
  )

  const visibleColors = useMemo(() => {
    return visibleDatasets.map((ds) => {
      const fullIndex = datasets.findIndex((item) => getSeriesKey(item) === getSeriesKey(ds))
      return seriesColorsProp[fullIndex] ?? seriesColorsProp[0] ?? '#008FFB'
    })
  }, [visibleDatasets, datasets, seriesColorsProp])

  const { series, options, chartKey } = useMemo(() => {
    const isLargeDataset = isIntradayView ? resolution === '1min' : labels.length > 100
    const isCompact = viewport.isMobile

    if (isIntradayView) {
      const intradaySeries = buildIntradaySeries(
        viewDate,
        resolution,
        labels,
        visibleDatasets,
        isTodayView
      )

      nameToKeyRef.current = new Map(intradaySeries.map((s) => [s.name, s.seriesKey]))

      const base = moment(viewDate, 'YYYY-MM-DD')
      const xMin =
        resolution === '30min'
          ? base.clone().startOf('day').add(30, 'minutes').valueOf()
          : base.clone().startOf('day').valueOf()
      const xMax = isTodayView
        ? moment().valueOf()
        : resolution === '30min'
        ? base.clone().add(1, 'day').startOf('day').valueOf()
        : base.clone().endOf('day').valueOf()

      const opts = {
        colors: visibleColors,
        chart: {
          id: 'live-data-intraday',
          zoom: {
            enabled: true,
            type: 'x' as const,
            autoScaleYaxis: true,
            allowMouseWheelZoom: !isCompact,
          },
          animations: {
            enabled: !isLargeDataset,
            dynamicAnimation: { enabled: !isLargeDataset },
          },
          toolbar: {
            show: true,
            offsetY: isCompact ? -4 : 0,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
            autoSelected: 'zoom' as const,
          },
          pan: { enabled: true },
          selection: { enabled: false },
          redrawOnParentResize: true,
          redrawOnWindowResize: true,
          events: {
            zoomed: (_chartCtx: unknown, { xaxis }: { xaxis?: { min?: number; max?: number } }) => {
              if (xaxis?.min != null && xaxis?.max != null) {
                zoomStateRef.current = { min: xaxis.min, max: xaxis.max }
              }
            },
            beforeResetZoom: () => {
              zoomStateRef.current = null
            },
            legendClick: (_chart: unknown, seriesIndex: number, opts: { seriesIndex?: number }) => {
              const idx = opts?.seriesIndex ?? seriesIndex
              const seriesName = intradaySeries[idx]?.name
              const key = seriesName ? nameToKeyRef.current.get(seriesName) : undefined
              if (key) onSeriesToggle(key)
              return false
            },
          },
        },
        dataLabels: { enabled: false },
        markers: {
          size: isLargeDataset || isCompact ? 0 : 2,
          strokeWidth: 0,
          hover: { sizeOffset: isCompact ? 4 : 2 },
        },
        stroke: { curve: 'smooth' as const, width: isCompact ? 2 : 2.5, dashArray: 0 },
        grid: {
          padding: { left: isCompact ? 4 : 12, right: isCompact ? 8 : 16, top: 8, bottom: 0 },
          xaxis: { lines: { show: true } },
        },
        legend: {
          show: true,
          onItemClick: { toggleDataSeries: false },
          onItemHover: { highlightDataSeries: true },
          position: 'bottom' as const,
          horizontalAlign: 'center' as const,
          fontSize: isCompact ? '11px' : '12px',
          itemMargin: { horizontal: isCompact ? 8 : 12, vertical: 4 },
          height: isCompact ? 56 : undefined,
        },
        tooltip: {
          shared: true,
          intersect: false,
          followTouchMove: true,
          x: {
            formatter: (val: number) => moment(val).format('D MMM YYYY, HH:mm'),
          },
        },
        xaxis: {
          type: 'datetime' as const,
          min: zoomStateRef.current?.min ?? xMin,
          max: zoomStateRef.current?.max ?? xMax,
          tickAmount: isCompact ? (viewport.isLandscape ? 8 : 5) : 12,
          labels: {
            style: { fontSize: isCompact ? '10px' : '12px' },
            datetimeUTC: false,
            showDuplicates: false,
            rotate: isCompact && !viewport.isLandscape ? -45 : 0,
            formatter: (_val: string, ts?: number) => moment(ts ?? _val).format('HH:mm'),
          },
        },
        yaxis: {
          title: {
            text: isCompact ? '' : intl.formatMessage({ id: 'TAGAWATT.YAXIS_TITLE' }),
            style: { fontSize: isCompact ? '11px' : '13px', fontWeight: 600 },
          },
          labels: {
            style: { fontSize: isCompact ? '10px' : '12px' },
            formatter: (val: number) => (val >= 1000 ? `${(val / 1000).toFixed(1)}k` : `${val}`),
          },
        },
      }

      return {
        series: intradaySeries.map(({ name, data }) => ({ name, data })),
        options: opts,
        chartKey: `intraday-${graphType}-${resolution}-${viewDate}-${chartType}-${visibleDatasets.length}`,
      }
    }

    const categorySeries = visibleDatasets.map((ds) => ({
      name: ds.name,
      seriesKey: getSeriesKey(ds),
      data: ds.data,
    }))

    nameToKeyRef.current = new Map(categorySeries.map((s) => [s.name, s.seriesKey]))

    const opts = {
      colors: visibleColors,
      chart: {
        id: 'live-data-category',
        zoom: {
          enabled: true,
          type: 'x' as const,
          autoScaleYaxis: true,
          allowMouseWheelZoom: !isCompact,
        },
        animations: {
          enabled: !isLargeDataset,
          dynamicAnimation: { enabled: !isLargeDataset },
        },
        toolbar: {
          show: true,
          offsetY: isCompact ? -4 : 0,
          tools: {
            download: true,
            selection: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
          autoSelected: 'zoom' as const,
        },
        pan: { enabled: true },
        selection: { enabled: false },
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        events: {
          zoomed: (_chartCtx: unknown, { xaxis }: { xaxis?: { min?: number; max?: number } }) => {
            if (xaxis?.min != null && xaxis?.max != null) {
              zoomStateRef.current = { min: xaxis.min, max: xaxis.max }
            }
          },
          beforeResetZoom: () => {
            zoomStateRef.current = null
          },
          legendClick: (
            _chart: unknown,
            seriesIndex: number,
            optsArg: { seriesIndex?: number }
          ) => {
            const idx = optsArg?.seriesIndex ?? seriesIndex
            const seriesName = categorySeries[idx]?.name
            const key = seriesName ? nameToKeyRef.current.get(seriesName) : undefined
            if (key) onSeriesToggle(key)
            return false
          },
        },
      },
      dataLabels: { enabled: false },
      markers: {
        size: isLargeDataset || isCompact ? 0 : 2,
        strokeWidth: 0,
        hover: { sizeOffset: isCompact ? 4 : 2 },
      },
      stroke: { curve: 'smooth' as const, width: isCompact ? 2 : 2.5, dashArray: 0 },
      grid: {
        padding: { left: isCompact ? 4 : 12, right: isCompact ? 8 : 16, top: 8, bottom: 0 },
      },
      legend: {
        show: true,
        onItemClick: { toggleDataSeries: false },
        onItemHover: { highlightDataSeries: true },
        position: 'bottom' as const,
        fontSize: isCompact ? '11px' : '12px',
        itemMargin: { horizontal: isCompact ? 8 : 12, vertical: 4 },
      },
      tooltip: { shared: true, intersect: false, followTouchMove: true },
      xaxis: {
        categories: labels,
        ...(zoomStateRef.current
          ? { min: zoomStateRef.current.min, max: zoomStateRef.current.max }
          : {}),
        tickAmount: isLargeDataset ? 16 : isCompact ? 4 : 6,
        labels: {
          show: true,
          rotate: labels.length > 5 ? (isCompact ? -45 : -35) : 0,
          rotateAlways: labels.length > 5,
          trim: true,
          hideOverlappingLabels: true,
          maxHeight: isCompact ? 72 : 120,
          style: { fontSize: isCompact ? '10px' : '12px' },
          showDuplicates: false,
        },
      },
      yaxis: {
        title: {
          text: isCompact ? '' : intl.formatMessage({ id: 'TAGAWATT.YAXIS_TITLE' }),
          style: { fontSize: isCompact ? '11px' : '13px', fontWeight: 600 },
        },
        labels: {
          style: { fontSize: isCompact ? '10px' : '12px' },
          formatter: (val: number) => (val >= 1000 ? `${(val / 1000).toFixed(1)}k` : `${val}`),
        },
      },
    }

    return {
      series: categorySeries.map(({ name, data }) => ({ name, data })),
      options: opts,
      chartKey: `category-${graphType}-${chartType}-${labels.length}-${visibleDatasets.length}`,
    }
  }, [
    isIntradayView,
    resolution,
    viewDate,
    labels,
    visibleDatasets,
    visibleColors,
    isTodayView,
    graphType,
    chartType,
    viewport,
    intl,
    onSeriesToggle,
  ])

  const enterFullscreen = useCallback(async () => {
    onFullscreenChange(true)
    try {
      const el = containerRef.current
      if (el?.requestFullscreen) {
        await el.requestFullscreen()
      }
    } catch {
      // overlay fallback still works without native fullscreen
    }
  }, [onFullscreenChange])

  const exitFullscreen = useCallback(async () => {
    onFullscreenChange(false)
    if (document.fullscreenElement && document.exitFullscreen) {
      await document.exitFullscreen()
    }
  }, [onFullscreenChange])

  useEffect(() => {
    const onFullscreenChangeEvent = () => {
      if (!document.fullscreenElement) {
        onFullscreenChange(false)
      }
    }
    document.addEventListener('fullscreenchange', onFullscreenChangeEvent)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChangeEvent)
  }, [onFullscreenChange])

  useEffect(() => {
    if (!isFullscreen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitFullscreen()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isFullscreen, exitFullscreen])

  const showNoIntradayPoints = useMemo(() => {
    if (!isIntradayView || visibleDatasets.length === 0) return false
    if (resolution === '1min') {
      return !visibleDatasets.some((ds) => ds.minute_data && Object.keys(ds.minute_data).length > 0)
    }
    return visibleDatasets.every((ds) => !ds.data || ds.data.length === 0)
  }, [isIntradayView, resolution, visibleDatasets])

  const showNoCategoryPoints = useMemo(() => {
    if (isIntradayView || visibleDatasets.length === 0) return false
    return !visibleDatasets.some((ds) => Array.isArray(ds.data) && ds.data.length > 0)
  }, [isIntradayView, visibleDatasets])

  if (datasets.length === 0) return null

  const chartBody = (
    <>
      {viewport.isMobile && (
        <style>{`
          .live-data-chart-container .apexcharts-toolbar,
          .live-data-chart-fullscreen .apexcharts-toolbar {
            gap: 2px;
          }
          .live-data-chart-container .apexcharts-toolbar > div,
          .live-data-chart-fullscreen .apexcharts-toolbar > div {
            min-width: 28px;
            min-height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .live-data-chart-container .apexcharts-toolbar svg,
          .live-data-chart-fullscreen .apexcharts-toolbar svg {
            width: 18px;
            height: 18px;
          }
        `}</style>
      )}
      <div className='d-flex align-items-center justify-content-between mb-2 px-1'>
        {viewport.isMobile && !isFullscreen ? (
          <span className='fs-8 text-gray-500'>
            {intl.formatMessage({ id: 'TAGAWATT.CHART_MOBILE_HINT' })}
          </span>
        ) : (
          <span />
        )}
        <button
          type='button'
          className={`btn btn-sm btn-light-primary ${viewport.isMobile ? 'btn-icon' : ''}`}
          style={viewport.isMobile ? { minWidth: 36, minHeight: 36 } : undefined}
          onClick={isFullscreen ? exitFullscreen : enterFullscreen}
          aria-label={intl.formatMessage({
            id: isFullscreen ? 'TAGAWATT.EXIT_FULLSCREEN' : 'TAGAWATT.ENTER_FULLSCREEN',
          })}
        >
          <i
            className={`fa ${isFullscreen ? 'fa-compress' : 'fa-expand'}${
              viewport.isMobile ? '' : ' me-1'
            }`}
          />
          {!viewport.isMobile &&
            intl.formatMessage({
              id: isFullscreen ? 'TAGAWATT.EXIT_FULLSCREEN' : 'TAGAWATT.ENTER_FULLSCREEN',
            })}
        </button>
      </div>

      {visibleDatasets.length === 0 ? (
        <div className='py-10 text-center text-muted'>
          {intl.formatMessage({ id: 'TAGAWATT.ALL_SERIES_HIDDEN' })}
        </div>
      ) : showNoIntradayPoints || showNoCategoryPoints ? (
        <div className='py-10 text-center text-muted'>{intl.formatMessage({ id: 'NODATA' })}</div>
      ) : (
        <ReactApexChart
          key={chartKey}
          options={options as any}
          series={series}
          type={graphType as 'line' | 'area' | 'bar' | 'radar'}
          height={chartHeight}
          width='100%'
        />
      )}
    </>
  )

  if (isFullscreen) {
    return (
      <div
        ref={containerRef}
        className='live-data-chart-fullscreen'
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 2000,
          background: '#fff',
          padding: viewport.isMobile ? '12px 8px' : '20px 24px',
          overflow: 'auto',
          touchAction: 'pan-y',
        }}
      >
        {chartBody}
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className='w-100 position-relative live-data-chart-container'
      style={{ minHeight: chartHeight, touchAction: 'pan-y' }}
    >
      {chartBody}
    </div>
  )
}

export default LiveDataChart
